import {match} from 'ts-pattern';

import {BusinessCaseState, BusinessCaseType} from '@dms/api/sales';

import {isNotNilOrEmpty, Nullish} from 'shared/web';

export type TradeInViewState =
  | 'EMPTY'
  | 'EMPTY_READONLY'
  | 'EMPTY_INVOICE_BLOCKED'
  | 'LINKED_EDITABLE'
  | 'LINKED_READONLY';

const READ_ONLY_STATES: BusinessCaseState[] = ['CLOSED', 'UNSUCCESSFUL'];

// A final invoice has been issued once the sale BC reaches INVOICE (or any later live state).
// CLOSED / UNSUCCESSFUL are handled by READ_ONLY_STATES, so they stay out of this set.
const INVOICE_ISSUED_STATES: BusinessCaseState[] = ['INVOICE', 'PAID'];

interface GetTradeInViewStateParams {
  businessCaseType: BusinessCaseType | Nullish;
  businessCaseState: BusinessCaseState;
  linkedTradeInBusinessCaseId: string | Nullish;
  isVehicleAvailable?: boolean;
}

export const getTradeInViewState = (params: GetTradeInViewStateParams): TradeInViewState =>
  match({
    isLinked: isNotNilOrEmpty(params.linkedTradeInBusinessCaseId),
    isReadOnly: READ_ONLY_STATES.includes(params.businessCaseState),
    // Both SELLING and BUYING hosts issue a final invoice once the BC reaches INVOICE state.
    isInvoiceBlocked:
      (params.businessCaseType === 'SELLING' || params.businessCaseType === 'BUYING') &&
      INVOICE_ISSUED_STATES.includes(params.businessCaseState),
    isVehicleSold:
      params.businessCaseType === 'SELLING' &&
      params.businessCaseState === 'OFFER' &&
      params.isVehicleAvailable === false,
  })
    .with({isLinked: true, isReadOnly: true}, () => 'LINKED_READONLY' as const)
    .with({isLinked: true}, () => 'LINKED_EDITABLE' as const)
    .with({isLinked: false, isInvoiceBlocked: true}, () => 'EMPTY_INVOICE_BLOCKED' as const)
    .with({isLinked: false, isReadOnly: true}, () => 'EMPTY_READONLY' as const)
    .with({isLinked: false, isVehicleSold: true}, () => 'EMPTY_READONLY' as const)
    .with({isLinked: false}, () => 'EMPTY' as const)
    .exhaustive();
